/**
 * Projekt-Dateibaum → flache Pfade + Chat-Mention-Erkennung (@Datei).
 */

export function flattenProjectFiles(tree, out = []) {
  const nodes = Array.isArray(tree) ? tree : [];
  for (const node of nodes) {
    if (!node) continue;
    if (node.type === 'file' && node.path) {
      const path = String(node.path).replace(/\\/g, '/');
      const name = String(node.name || path.split('/').pop() || path);
      out.push({ path, name });
    }
    if (Array.isArray(node.children) && node.children.length) {
      flattenProjectFiles(node.children, out);
    }
  }
  return out;
}

/**
 * Sucht am Cursor nach @query oder einem pfad-/dateiartigen Token.
 * @returns {{ start: number, end: number, query: string, triggeredByAt: boolean } | null}
 */
export function getFileMentionAtCursor(text, cursorPos) {
  const value = String(text || '');
  const pos = Math.max(0, Math.min(cursorPos ?? value.length, value.length));
  const before = value.slice(0, pos);

  const atMatch = before.match(/(^|[\s([{`"'„])@([^\s@]*)$/);
  if (atMatch) {
    const query = atMatch[2] || '';
    const start = pos - query.length - 1; // inkl. @
    return { start, end: pos, query, triggeredByAt: true };
  }

  // Pfad / Dateiname ohne @
  const bareMatch = before.match(/(^|[\s([{`"'„])([A-Za-z0-9_./\\-]{2,})$/);
  if (bareMatch) {
    const query = bareMatch[2] || '';
    const looksLikePath = /[./\\]/.test(query) || /\.[a-z0-9]{1,8}$/i.test(query);
    const looksLikeName = query.length >= 3 && /^[A-Za-z]/.test(query);
    if (looksLikePath || looksLikeName) {
      const start = pos - query.length;
      return { start, end: pos, query, triggeredByAt: false };
    }
  }

  return null;
}

function scoreFile(file, queryRaw) {
  const q = String(queryRaw || '')
    .replace(/^@/, '')
    .replace(/\\/g, '/')
    .toLowerCase();
  if (!q) return 1;
  const path = file.path.toLowerCase();
  const name = file.name.toLowerCase();
  if (name === q || path === q) return 1000;
  if (name.startsWith(q)) return 800 - Math.min(name.length, 100);
  if (path.endsWith('/' + q) || path.endsWith(q)) return 700;
  if (name.includes(q)) return 500 - name.indexOf(q);
  if (path.includes(q)) return 300 - path.indexOf(q);
  // Fuzzy: alle Zeichen der Query der Reihe nach im Namen
  let ti = 0;
  for (let i = 0; i < name.length && ti < q.length; i += 1) {
    if (name[i] === q[ti]) ti += 1;
  }
  if (ti === q.length) return 120;
  return 0;
}

export function filterProjectFiles(files, query, limit = 12) {
  const list = Array.isArray(files) ? files : [];
  const scored = [];
  for (const file of list) {
    const s = scoreFile(file, query);
    if (s > 0) scored.push({ ...file, score: s });
  }
  scored.sort((a, b) => b.score - a.score || a.path.localeCompare(b.path));
  return scored.slice(0, limit);
}

/** Ersetzt den Mention-Bereich durch @voll/pfad (mit Leerzeichen danach). */
export function applyFileMention(text, mention, filePath) {
  const value = String(text || '');
  const path = String(filePath || '').replace(/\\/g, '/');
  const start = mention?.start ?? 0;
  const end = mention?.end ?? start;
  const insert = `@${path} `;
  return {
    text: value.slice(0, start) + insert + value.slice(end),
    cursor: start + insert.length,
  };
}
